/**
 * Reconhecer o leitor de código de barras fora de um campo — F206.
 *
 * O dono: *"eles leem, faz o barulho e não acontece nada"*. O leitor é um
 * teclado que digita o código em poucos milissegundos e aperta Enter; sem um
 * campo com foco, essas teclas iam para lugar nenhum.
 *
 * O que separa leitor de gente é o RITMO: o leitor manda uma tecla a cada
 * poucos ms; ninguém digita a 50 ms por tecla de forma sustentada. Então só
 * vira bipe uma sequência de pelo menos 4 caracteres, cada um a menos de
 * `MAX_GAP_MS` do anterior, terminada em Enter.
 *
 * Puro de propósito — recebe tecla e instante —, para o teste não depender de
 * relógio nem de DOM.
 */

export const MAX_GAP_MS = 50
export const MIN_LENGTH = 4

export type ScanCapture = {
  /**
   * Uma tecla. Devolve o código lido quando esta tecla fecha um bipe (o
   * Enter), e `null` nas outras.
   */
  readonly key: (key: string, at: number) => string | null
  readonly reset: () => void
}

export const createScanCapture = (): ScanCapture => {
  let buffer = ''
  let last = -Infinity

  const reset = (): void => {
    buffer = ''
    last = -Infinity
  }

  const key = (pressed: string, at: number): string | null => {
    const gap = at - last
    last = at

    if (pressed === 'Enter') {
      const code = gap <= MAX_GAP_MS ? buffer : ''
      buffer = ''
      return code.length >= MIN_LENGTH ? code : null
    }

    // Só caracteres imprimíveis entram; Shift, Tab, setas, F-teclas não.
    if (pressed.length !== 1) return null

    // Demorou: o que havia no buffer era gente digitando, e recomeça daqui.
    buffer = gap <= MAX_GAP_MS ? buffer + pressed : pressed
    return null
  }

  return { key, reset }
}

/** Foco num campo onde a pessoa escreve: o bipe é do campo, não do global. */
export const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag !== 'INPUT') return false
  const type = (target as HTMLInputElement).type
  return !['button', 'checkbox', 'radio', 'submit', 'reset', 'file'].includes(
    type
  )
}
